import type { AgentRun } from '@timato/shared';

/**
 * Totals over agent runs. Kept apart from `data/stats.ts` on purpose: nothing in there may ever reach this,
 * because agent time is not the person's time (architecture §11.3).
 */
export const isInFlight = (r: AgentRun) => !r.endedAt;

export const runSeconds = (r: AgentRun, now: Date) =>
  Math.max(0, Math.round(((r.endedAt ? Date.parse(r.endedAt) : now.getTime()) - Date.parse(r.startedAt)) / 1000));

/** Agent time on a set of runs. In-flight runs count from their start to now, so the panel ticks. */
export const agentSeconds = (runs: AgentRun[], now: Date) => runs.reduce((a, r) => a + runSeconds(r, now), 0);

/**
 * Agent spend, in whole cents, or null when nothing reported any. Null is not zero: cost only exists once the
 * agent's host reports usage, and "$0.00" would be a claim we cannot make.
 */
export function agentCostCents(runs: AgentRun[]): number | null {
  const reported = runs.filter((r) => r.costCents != null);
  return reported.length ? reported.reduce((a, r) => a + (r.costCents ?? 0), 0) : null;
}

/** Agents on a set of runs, in first-seen order: "Claude Code, Codex" (9c rule 06). */
export const agentNames = (runs: AgentRun[]) => [...new Set(runs.map((r) => r.agent))];

export const formatCost = (cents: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(cents / 100);

/** Agent time per project, for the sidebar's rows (TW-46). Shown beside each project's own figure, never added to it. */
export function agentByProject(runs: AgentRun[], now: Date): Map<string | null, number> {
  const out = new Map<string | null, number>();
  for (const r of runs) out.set(r.projectId, (out.get(r.projectId) ?? 0) + runSeconds(r, now));
  return out;
}
